import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, Navigate, useLocation } from 'react-router';
import { Link as MuiLink, Box } from '@mui/material';
import type { BoxProps } from '@mui/material';
import { H1 } from '../../components/H1/index.ts';
import { H2 } from '../../components/H2/index.ts';
import { Paragraph } from '../../components/Paragraph/index.ts';
import Logo from '../../components/Logo/index.tsx';
import { PageFooter } from '../Page/PageFooter.tsx';
import { generateLoginUrl, isMagicAccessToken } from '../../services/auth.ts';

export function PageErrorBoundaryLayout({
    hiddenHeader = false,
    hiddenFooter = false,
    content,
    fullHeight = false,
    isAuthError = false,
    ...rootProps
}: {
    hiddenHeader?: boolean;
    hiddenFooter?: boolean;
    content?: ReactNode;
    fullHeight?: boolean;
    isAuthError?: boolean;
} & Omit<BoxProps, 'title'>) {
    // Redirect in case it is an auth error. This is probably the wrong place for this. Before, we handled this in
    // the relay environment, but there, we could not push a location to the router.
    const location = useLocation();
    if (isAuthError) {
        // In case the current route is a magic link route
        if (isMagicAccessToken()) {
            return <Navigate to={'/_/expired' + location.pathname} />;
        }
        return <Navigate to={generateLoginUrl(location)} />;
    }
    return (
        <Box
            {...rootProps}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: fullHeight ? '100vh' : '100%',
                '& > header': {
                    alignSelf: 'center',
                    margin: '3em 0 5em',
                },
                '& > article': {
                    flexGrow: 1,
                    alignSelf: 'center',
                    margin: '0 3em 3em',
                    textAlign: 'center',
                },
            }}
        >
            <header>
                {!hiddenHeader && (
                    <Link to="/">
                        <Logo />
                    </Link>
                )}
            </header>
            <article role="alert">
                {content ? (
                    content
                ) : (
                    <>
                        <H1
                            sx={(theme) => ({
                                fontSize: 90,
                                marginBottom: '70px !important',

                                [theme.breakpoints.up('sm')]: {
                                    fontSize: 120,
                                },
                            })}
                        >
                            Oops!
                        </H1>
                        <H2>
                            <FormattedMessage
                                defaultMessage="There was a problem serving the requested page."
                                description="Error page title"
                            />
                        </H2>
                        {/*<Paragraph>*/}
                        {/*    <FormattedMessage*/}
                        {/*        defaultMessage="Usually this means that unexpected error happened while processing your request."*/}
                        {/*        description="Error page description"*/}
                        {/*    />*/}
                        {/*</Paragraph>*/}
                        <Paragraph>
                            <FormattedMessage
                                defaultMessage="The problem may be temporary, <b>try reloading the page</b>."
                                description="Error page description"
                            />
                        </Paragraph>
                        <Paragraph>
                            <FormattedMessage
                                defaultMessage="If the problem persists, <link>contact us</link> and we'll help you get on your way."
                                description="Tip description"
                                values={{
                                    link: (chunks) => (
                                        <MuiLink href="mailto:support@binosight.com">
                                            {chunks}
                                        </MuiLink>
                                    ),
                                }}
                            />
                        </Paragraph>
                    </>
                )}
            </article>
            {!hiddenFooter && <PageFooter />}
        </Box>
    );
}
